import {
  FlightPassengerSelectors,
  ParentState,
  useChildMachineSelector,
  useChildMachineState,
} from "@capone/checkout";
import clsx from "clsx";
import {
  B2BSpinner,
  handleUpdateSelectedTravelersList,
  isAdult,
  isInfant,
  LoadingIndicator,
} from "halifax";
import React, { useMemo, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  ADD_FREQUENT_FLYER,
  IPerson,
  ITravelerStepErrors,
  SAVED_NEW_TRAVELER,
} from "redmond";
import { PassengerErrorModal, PassportModal } from "../..";
import { trackEvent } from "../../../../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  getExperimentVariant,
  PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
  useExperiments,
} from "../../../../../context/experiments";
import { personToIPerson } from "../../../../../utils/personToIPerson";
import { Event, TEvent } from "../../../state/events";
import { PackagesMachineContext } from "../../../state/types";
import { DesktopPackageBookValidationError } from "../../DesktopPackagesBookWorkflow";
import { InfantSeatPickerModal } from "../../InfantSeatPickerModal";
import { FlightTravelerSelectDropdownForm } from "../FlightTravelerSelectDropdownForm";
import { FlightBookPassengerSelectionConnectorProps } from "./container";
import "./styles.scss";
import * as constants from "./textConstants";

export interface IFlightBookPassengerSelectionProps
  extends FlightBookPassengerSelectionConnectorProps,
    RouteComponentProps {
  isMobile?: boolean;
  validationErrorTypes?: DesktopPackageBookValidationError[];
}

export const FlightBookPassengerSelection = ({
  isMobile,
  validationErrorTypes,
  searchedAdultsCount,
  searchedChildrenCount,
  tripDetails,
}: IFlightBookPassengerSelectionProps) => {
  const [showErrors, setShowErrors] = useState<ITravelerStepErrors>({
    phone: false,
    email: false,
    travelerSelect: false,
    corpPrimaryTraveler: false,
  });

  const [, passengerChildMachineSend] = useChildMachineState<
    TEvent,
    PackagesMachineContext
  >(ParentState.passengerInformation);

  const travelers = useChildMachineSelector(
    ParentState.passengerInformation,
    FlightPassengerSelectors.getUserPassengers
  );

  // all travelers excluding lap infants
  const selectedTravelerIds = useChildMachineSelector(
    ParentState.passengerInformation,
    FlightPassengerSelectors.getSelectedPassengerIds
  );

  const selectedLapInfantIds = useChildMachineSelector(
    ParentState.passengerInformation,
    FlightPassengerSelectors.getSelectedLapInfantIds
  );

  const allSelectedTravelerIds = [
    ...selectedTravelerIds,
    ...selectedLapInfantIds,
  ];

  const passengersLoading = useChildMachineSelector(
    ParentState.passengerInformation,
    FlightPassengerSelectors.getIsPassengerInformationLoading
  );

  const hasError = useChildMachineSelector(
    ParentState.passengerInformation,
    FlightPassengerSelectors.getPassengerErrorOpen
  );

  const currentInfantId = useChildMachineSelector(
    ParentState.passengerInformation,
    FlightPassengerSelectors.getCurrentInfantId
  );

  const selectedTravelersList = useChildMachineSelector(
    ParentState.passengerInformation,
    FlightPassengerSelectors.getUserSelectedTravelersList
  );

  const expState = useExperiments();

  const isPackageCheckoutOptimizationExperiment =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_CHECKOUT_OPTIMIZATIONS_V1
    ) === AVAILABLE;

  const handleSelectPassenger = (
    selectedPassenger: IPerson,
    singleTravelerWorkflow?: boolean
  ) => {
    if (hasError) {
      passengerChildMachineSend(Event.CLEAR_PASSENGER_INFORMATION_ERROR);
    }

    if (selectedPassenger) {
      passengerChildMachineSend({
        type: Event.SET_CURRENT_PASSENGER,
        passenger: selectedPassenger,
      });
    }
    passengerChildMachineSend({
      type: Event.SELECT_PASSENGER,
      passengerId: selectedPassenger?.id,
      singleTravelerWorkflow,
    });
  };

  const handleUpdateSelectedTraveler = (traveler: IPerson) => {
    // corporate needs the userSelectedTravelersList state property to maintain and edit whole traveler objects and
    // to update multiple traveler profiles at once through the update_multiple endpoint
    handleUpdateSelectedTravelersList(
      traveler,
      selectedTravelersList,
      (userSelectedTravelersList) =>
        passengerChildMachineSend({
          type: Event.SET_USER_SELECTED_TRAVELERS_LIST,
          userSelectedTravelersList,
        })
    );

    if (!allSelectedTravelerIds.includes(traveler.id)) {
      handleSelectPassenger(traveler);
    }
  };

  const mappedTravelers = useMemo(
    () => travelers.map(personToIPerson),
    [travelers]
  );

  const handleUpdatePassenger = (passenger: IPerson) => {
    passengerChildMachineSend({
      type: Event.UPDATE_PASSENGER,
      person: {
        ...passenger,
        passport:
          passenger.passport?.countryOfIssue &&
          passenger.passport?.expiration &&
          passenger.passport?.number
            ? passenger.passport
            : undefined,
      },
      onUpdate: true,
      currentTravelers: mappedTravelers,
    });
  };

  const handleInfantPickerModalClose = () => {
    passengerChildMachineSend(Event.CLEAR_CURRENT_INFANT_ID);
  };

  const handleInfantPickerModalContinue = (seatType: "OwnSeat" | "OnLap") => {
    passengerChildMachineSend({
      type: Event.ON_INFANT_MODAL_CONTINUE,
      seatType,
    });
    handleInfantPickerModalClose();
  };

  React.useEffect(() => {
    if (
      isMobile &&
      isPackageCheckoutOptimizationExperiment &&
      selectedTravelerIds.length === 0
    ) {
      const adultTravelers = mappedTravelers.filter((traveler) =>
        isAdult(traveler.dateOfBirth)
      );
      const childTravelers = mappedTravelers.filter(
        (traveler) =>
          !isAdult(traveler.dateOfBirth) && !isInfant(traveler.dateOfBirth)
      );

      // only auto-select if searched traveler count matches the number of existing travelers
      if (adultTravelers.length === searchedAdultsCount) {
        new Array(searchedAdultsCount).fill(0).forEach((_, index) => {
          const travelerToSelect = adultTravelers[index];
          if (travelerToSelect) {
            handleSelectPassenger(travelerToSelect);
          }
        });
      }

      if (childTravelers.length === searchedChildrenCount) {
        new Array(searchedChildrenCount).fill(0).forEach((_, index) => {
          const travelerToSelect = childTravelers[index];
          if (travelerToSelect) {
            handleSelectPassenger(travelerToSelect);
          }
        });
      }
    }
  }, [
    isMobile,
    isPackageCheckoutOptimizationExperiment,
    travelers,
    selectedTravelerIds,
  ]);

  return (
    <>
      {passengersLoading ? (
        <LoadingIndicator
          className="flight-book-passenger-selection-loading-indicator"
          indicatorSize="small"
          indicator={B2BSpinner}
          message={constants.UPDATE_TEXT}
        />
      ) : (
        <FlightTravelerSelectDropdownForm
          className={clsx("packages-flight-book-passenger-selection-root", {
            error: validationErrorTypes?.includes("flight-travelers"),
            "cx-optimization-experiment":
              isPackageCheckoutOptimizationExperiment,
          })}
          tripDetails={tripDetails}
          showErrors={showErrors}
          loading={passengersLoading}
          travelers={mappedTravelers}
          disableEditTravelerInfo={false}
          selectedTravelerIds={allSelectedTravelerIds}
          selectedTravelersList={selectedTravelersList}
          setShowErrors={setShowErrors}
          updateSelectedTraveler={handleUpdateSelectedTraveler}
          removeSelectedTraveler={(traveler) => {
            if (selectedTravelerIds.includes(traveler.id)) {
              handleSelectPassenger(traveler);
            }
          }}
          updateUserPassenger={(traveler, isNewTraveler) => {
            const freqFlyerKeys = Object.keys(traveler.frequentFlyer);
            if (
              freqFlyerKeys.length > 0 &&
              traveler.frequentFlyer !==
                travelers.find((t) => t.id === traveler.id)?.frequentFlyer
            ) {
              trackEvent({
                eventName: ADD_FREQUENT_FLYER,
                properties: {
                  frequent_flyer_program:
                    freqFlyerKeys.length > 0
                      ? freqFlyerKeys[freqFlyerKeys.length - 1]
                      : "",
                },
              });
            }

            if (isNewTraveler) {
              trackEvent({
                eventName: SAVED_NEW_TRAVELER,
                properties: {
                  funnel: "packages",
                },
              });
            }

            handleUpdatePassenger(traveler);
          }}
        />
      )}
      <InfantSeatPickerModal
        currentInfantToSelectSeat={currentInfantId}
        handleInfantPickerModalClose={handleInfantPickerModalClose}
        onContinue={handleInfantPickerModalContinue}
      />
      <PassengerErrorModal />
      <PassportModal />
    </>
  );
};
